import React, { useState } from "react";
import "./TechnicalIndicators.scss";
import { CompanyAnalytics } from "../../../services/analyticsService";
import analyticsService from "../../../services/analyticsService";

interface TechnicalIndicator {
  indicator: string;
  timeframe: string;
  currentValue: string;
  whyImportant: string;
  buySignal: string;
  sellSignal: string;
  signal: "buy" | "sell" | "neutral";
}

interface TechnicalIndicatorsData {
  indicators: TechnicalIndicator[];
  overallRating: "buy" | "sell" | "neutral";
  ratingScore: number; // 0-100
}

interface TechnicalIndicatorsProps {
  companyId: number;
  analytics: CompanyAnalytics | null;
  data?: TechnicalIndicatorsData;
  onRefresh?: () => void;
}

const TechnicalIndicators: React.FC<TechnicalIndicatorsProps> = ({ companyId, analytics, data, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    analyticsService.refreshTechnicalIndicators(companyId).subscribe({
      next: () => {
        if (onRefresh) onRefresh();
        setRefreshing(false);
      },
      error: (err) => {
        console.error("Failed to refresh technical indicators:", err);
        setRefreshing(false);
      }
    });
  };
  const getTechnicalDataFromAnalytics = (): TechnicalIndicatorsData => {
    if (!analytics?.technical) {
      return { overallRating: "neutral", ratingScore: 50, indicators: [] };
    }
    const tech = analytics.technical;
    const perf = analytics.performance;
    const indicators: TechnicalIndicator[] = [];

    if (tech.rsi !== null && tech.rsi !== undefined) {
      let rsiSignal: "buy" | "sell" | "neutral" = "neutral";
      if (tech.rsi < 30) rsiSignal = "buy";
      else if (tech.rsi > 70) rsiSignal = "sell";
      indicators.push({
        indicator: "RSI (14)", timeframe: "Daily", currentValue: tech.rsi.toFixed(2),
        whyImportant: "Overbought/Oversold", buySignal: "<30 (Oversold)", sellSignal: ">70 (Overbought)",
        signal: rsiSignal
      });
    }

    if (tech.ma_signal) {
      const maSignalLower = tech.ma_signal.toLowerCase();
      indicators.push({
        indicator: "Moving Averages", timeframe: "Daily", currentValue: tech.ma_signal,
        whyImportant: "Trend direction", buySignal: "Bullish crossover", sellSignal: "Bearish crossover",
        signal: maSignalLower.includes("buy") || maSignalLower.includes("bull") ? "buy" :
                maSignalLower.includes("sell") || maSignalLower.includes("bear") ? "sell" : "neutral"
      });
    }

    if (tech.support_level_1 && tech.resistance_level_1 && perf?.current_price) {
      let srSignal: "buy" | "sell" | "neutral" = "neutral";
      const distToSupport = ((perf.current_price - tech.support_level_1) / tech.support_level_1) * 100;
      const distToResistance = ((tech.resistance_level_1 - perf.current_price) / perf.current_price) * 100;
      if (distToSupport < 2) srSignal = "buy";
      else if (distToResistance < 2) srSignal = "sell";
      indicators.push({
        indicator: "Support/Resistance", timeframe: "-",
        currentValue: `₹${tech.support_level_1.toFixed(2)} / ₹${tech.resistance_level_1.toFixed(2)}`,
        whyImportant: "Entry/Exit points", buySignal: "Near support", sellSignal: "Near resistance",
        signal: srSignal
      });
    }

    if (perf?.week_52_high && perf?.week_52_low && perf?.current_price) {
      const range = perf.week_52_high - perf.week_52_low;
      const position = ((perf.current_price - perf.week_52_low) / range) * 100;
      let rangeSignal: "buy" | "sell" | "neutral" = "neutral";
      if (position < 30) rangeSignal = "buy";
      else if (position > 70) rangeSignal = "sell";
      indicators.push({
        indicator: "52-Week Range", timeframe: "Yearly", currentValue: `${position.toFixed(0)}% of range`,
        whyImportant: "Price positioning", buySignal: "Lower 30% of range", sellSignal: "Upper 30% of range",
        signal: rangeSignal
      });
    }

    const buyCount = indicators.filter(i => i.signal === "buy").length;
    const sellCount = indicators.filter(i => i.signal === "sell").length;
    const totalCount = indicators.length;
    let overallRating: "buy" | "sell" | "neutral" = "neutral";
    let ratingScore = 50;
    if (buyCount > sellCount && buyCount >= totalCount * 0.5) {
      overallRating = "buy";
      ratingScore = 50 + (buyCount / totalCount) * 50;
    } else if (sellCount > buyCount && sellCount >= totalCount * 0.5) {
      overallRating = "sell";
      ratingScore = 50 - (sellCount / totalCount) * 50;
    }
    return { indicators, overallRating, ratingScore: Math.round(ratingScore) };
  };

  const mockData: TechnicalIndicatorsData = {
    overallRating: "buy",
    ratingScore: 72,
    indicators: [
      {
        indicator: "Moving Averages (SMA/EMA 50, 200)",
        timeframe: "Daily/Weekly",
        currentValue: "Golden Cross",
        whyImportant: "Trend direction",
        buySignal: "Price > MA, Golden Cross",
        sellSignal: "Price < MA, Death Cross",
        signal: "buy"
      },
      {
        indicator: "RSI (14)",
        timeframe: "Daily",
        currentValue: "58",
        whyImportant: "Overbought/Oversold",
        buySignal: "<30 (Oversold)",
        sellSignal: ">70 (Overbought)",
        signal: "neutral"
      },
      {
        indicator: "MACD",
        timeframe: "Daily",
        currentValue: "Bullish Crossover",
        whyImportant: "Momentum shifts",
        buySignal: "Bullish crossover",
        sellSignal: "Bearish crossover",
        signal: "buy"
      },
      {
        indicator: "Bollinger Bands",
        timeframe: "Daily",
        currentValue: "Mid-range",
        whyImportant: "Volatility & reversals",
        buySignal: "Price at lower band",
        sellSignal: "Price at upper band",
        signal: "neutral"
      },
      {
        indicator: "Support/Resistance Levels",
        timeframe: "-",
        currentValue: "₹820 / ₹865",
        whyImportant: "Entry/Exit points",
        buySignal: "Near support",
        sellSignal: "Near resistance",
        signal: "buy"
      }
    ]
  };

  const technicalData = data || (analytics ? getTechnicalDataFromAnalytics() : mockData);

  const getSignalBadge = (signal: string) => {
    switch (signal) {
      case "buy":
        return <span className="tag is-success">Buy</span>;
      case "sell":
        return <span className="tag is-danger">Sell</span>;
      case "neutral":
        return <span className="tag is-warning">Neutral</span>;
      default:
        return null;
    }
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case "buy":
        return <span className="icon has-text-success"><i className="fas fa-arrow-up"></i></span>;
      case "sell":
        return <span className="icon has-text-danger"><i className="fas fa-arrow-down"></i></span>;
      case "neutral":
        return <span className="icon has-text-warning"><i className="fas fa-minus"></i></span>;
      default:
        return null;
    }
  };

  const getOverallRatingClass = (rating: string) => {
    switch (rating) {
      case "buy":
        return "is-success";
      case "sell":
        return "is-danger";
      case "neutral":
        return "is-warning";
      default:
        return "is-info";
    }
  };

  const getOverallRatingText = (rating: string) => {
    switch (rating) {
      case "buy":
        return "Strong Buy";
      case "sell":
        return "Strong Sell";
      case "neutral":
        return "Neutral/Hold";
      default:
        return "Neutral";
    }
  };

  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="technical-indicators mb-2">
      <div className="box py-3 px-3 mb-0">
        <div className="level mb-2">
          <div className="level-left">
            <div className="level-item">
              <h2 className="title is-6 mb-0">Technical Indicators</h2>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <div className="tags has-addons mb-0 mr-2">
                <span className="tag is-small">Rating</span>
                <span className={`tag is-small ${getOverallRatingClass(technicalData.overallRating)}`}>
                  {getOverallRatingText(technicalData.overallRating)} ({technicalData.ratingScore}%)
                </span>
              </div>
            </div>
            <div className="level-item">
              <button
                className={`button is-small is-info is-outlined mr-2 ${refreshing ? 'is-loading' : ''}`}
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh"
              >
                <span className="icon is-small">
                  <i className="fas fa-sync-alt"></i>
                </span>
              </button>
              <button
                className="button is-small is-primary is-outlined"
                onClick={() => setShowDetails(!showDetails)}
              >
                <span className="icon is-small">
                  <i className={`fas fa-${showDetails ? 'eye-slash' : 'info-circle'}`}></i>
                </span>
                <span>{showDetails ? 'Hide' : 'Details'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Compact Summary View */}
        <div className="columns is-mobile mb-2" style={{ fontSize: '0.8rem' }}>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Buy</p>
            <p className="title is-6 has-text-success mb-0">
              {technicalData.indicators.filter(i => i.signal === "buy").length}
            </p>
          </div>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Neutral</p>
            <p className="title is-6 has-text-warning mb-0">
              {technicalData.indicators.filter(i => i.signal === "neutral").length}
            </p>
          </div>
          <div className="column has-text-centered py-2">
            <p className="heading is-size-7 mb-1">Sell</p>
            <p className="title is-6 has-text-danger mb-0">
              {technicalData.indicators.filter(i => i.signal === "sell").length}
            </p>
          </div>
        </div>

        {/* Compact Table */}
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable is-narrow" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr className="is-size-7">
                <th>Indicator</th>
                <th>Value</th>
                <th className="has-text-centered">Signal</th>
                {showDetails && (
                  <>
                    <th>Buy If</th>
                    <th>Sell If</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {technicalData.indicators.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span className="has-text-weight-semibold">{item.indicator}</span>
                    <br />
                    <span className="tag is-light" style={{ fontSize: '0.65rem' }}>{item.timeframe}</span>
                  </td>
                  <td>
                    <span className="has-text-weight-bold">{item.currentValue}</span>
                    {showDetails && (
                      <>
                        <br />
                        <span className="has-text-grey is-size-7">{item.whyImportant}</span>
                      </>
                    )}
                  </td>
                  <td className="has-text-centered">
                    {getSignalBadge(item.signal)}
                  </td>
                  {showDetails && (
                    <>
                      <td className="has-text-success-dark is-size-7">{item.buySignal}</td>
                      <td className="has-text-danger-dark is-size-7">{item.sellSignal}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TechnicalIndicators;
